import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

type ThemeMode = "light" | "dark";

interface ThemeContextType {
  mode: ThemeMode;
  toggle: () => void;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function themeFromQuery(): ThemeMode | null {
  if (typeof window === "undefined") return null;
  const theme = new URLSearchParams(window.location.search).get("theme");
  if (theme === "dark" || theme === "light") return theme;
  return null;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const fromQuery = themeFromQuery();
    if (fromQuery) return fromQuery;
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("theme");
      if (stored === "dark" || stored === "light") return stored;
    }
    return "light";
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
    document.documentElement.classList.toggle("dark", mode === "dark");
    localStorage.setItem("theme", mode);
  }, [mode]);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("theme") && !url.searchParams.has("lang")) return;
    url.searchParams.delete("theme");
    url.searchParams.delete("lang");
    const search = url.searchParams.toString();
    window.history.replaceState(
      {},
      "",
      `${url.pathname}${search ? `?${search}` : ""}${url.hash}`,
    );
  }, []);

  const toggle = () => setMode((m) => (m === "light" ? "dark" : "light"));

  return (
    // Pasamos setMode en el value
    <ThemeContext.Provider value={{ mode, toggle, setMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme debe usarse dentro de ThemeProvider");
  return context;
};